import { useCallback, useState } from 'react';
import Cropper from 'react-easy-crop';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { useTranslation } from 'react-i18next';
import { cropAndCompressToWebP } from '../../utils/imageCompression';

export const PhotoCropModal = ({ open, imageSrc, onClose, onConfirm, busy = false }) => {
  const { t } = useTranslation();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropComplete = useCallback((_, areaPx) => setCroppedAreaPixels(areaPx), []);

  const handleConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    // Resize to 400×400 + WebP compress (50-150 KB target, 500 KB hard cap)
    const blob = await cropAndCompressToWebP(imageSrc, croppedAreaPixels);
    onConfirm(blob);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="photo-crop-modal">
        <DialogHeader>
          <DialogTitle>{t('settings.cropPhoto') || 'Adjust your photo'}</DialogTitle>
        </DialogHeader>
        <div className="relative w-full h-72 bg-black rounded-lg overflow-hidden">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>
        <div className="px-1 pt-3">
          <div className="flex items-center gap-3">
            <span className="text-xs text-text_muted w-10">Zoom</span>
            <Slider
              value={[zoom]}
              min={1}
              max={3}
              step={0.05}
              onValueChange={(v) => setZoom(v[0])}
              data-testid="photo-crop-zoom"
            />
          </div>
          <p className="text-xs text-text_muted mt-2">
            {t('settings.recommendedSquare') || 'Recommended: square format (1:1).'}
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="photo-crop-cancel">
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={busy || !croppedAreaPixels}
            variant="primary"
            data-testid="photo-crop-confirm"
          >
            {busy ? (t('common.loading') || 'Loading...') : (t('settings.uploadPhoto') || 'Save photo')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
